/**
 * @file calendly-webhook-event.schema.ts
 * @description Registro de cada webhook recebido do Calendly
 * (coleção 'calendlyWebhookEvents'). Serve a dois propósitos: idempotência e
 * auditoria de "por que esse agendamento não apareceu".
 *
 * O id do documento é determinístico (ver `buildCalendlyWebhookEventId`), e a
 * gravação usa `.doc(id).create()` — que falha com ALREADY_EXISTS quando o
 * evento já foi processado. Isso NÃO dá para fazer pelo BaseRepository, que
 * chama `.doc()` sem argumento e gera id automático: use `getFirestore()`
 * direto, como `upload-session.service.ts` já faz.
 */

import { z } from 'zod';

export const CalendlyWebhookEventStatusEnum = z.enum([
  'RECEIVED',
  'PROCESSED',
  /** Erro de processamento. Respondemos 200 assim mesmo; o reconciler reprocessa. */
  'FAILED',
  /** Evento válido mas sem ação (ex.: cancelamento de reagendamento). */
  'IGNORED',
]);
export type CalendlyWebhookEventStatus = z.infer<typeof CalendlyWebhookEventStatusEnum>;

export const CalendlyWebhookEventSchema = z.object({
  /** id do documento == hash determinístico do evento. */
  id: z.string(),
  partnerId: z.string(),
  /** 'invitee.created' | 'invitee.canceled' */
  event: z.string(),
  /** URI do invitee no payload — o identificador estável do evento. */
  payloadUri: z.string(),
  calendlyCreatedAt: z.coerce.date(),
  receivedAt: z.coerce.date(),
  processedAt: z.coerce.date().nullable().default(null),
  status: CalendlyWebhookEventStatusEnum,
  appointmentId: z.string().nullable().default(null),
  error: z.string().nullable().default(null),
  /** receivedAt + 30 dias — política de TTL do Firestore limpa o histórico. */
  expiresAt: z.coerce.date(),
});
export type CalendlyWebhookEvent = z.infer<typeof CalendlyWebhookEventSchema>;

/**
 * Chave de deduplicação. Determinística nos campos que o Calendly garante
 * estáveis entre reentregas do mesmo evento.
 *
 * O hash em si é calculado na API (`calendly-webhook.service.ts`), que tem
 * acesso a `crypto`; aqui fica só a montagem da string, para que o formato seja
 * o mesmo em qualquer lugar que precise reconstruí-la.
 */
export function buildCalendlyWebhookDedupeInput(params: {
  partnerId: string;
  event: string;
  payloadUri: string;
  calendlyCreatedAt: string;
}): string {
  return `${params.partnerId}:${params.event}:${params.payloadUri}:${params.calendlyCreatedAt}`;
}
